/**
 * Real local capture — getUserMedia / getDisplayMedia, device switching, and the
 * two CLIENT-SIDE processing chains:
 *
 *   mic    → RNNoise AudioWorklet    (media/audio/**)
 *   camera → segmentation compositor (media/video/**)
 *
 * The published tracks are whatever comes out of those chains, so the SFU
 * forwards already-processed media and never transcodes. Both chains are
 * optional and fail soft: if either cannot start, the RAW track is published
 * and `processing` carries the reason for the settings UI to show.
 */
import { LAYER_SPECS, type QualityProfile } from '@cyphex/types';
import { hasEchoCancelledSystemAudio, isDesktopShell } from '@/features/desktop/bridge';
import { micConstraints } from './audio/audioConstraints';
import { createDenoiseController, type DenoiseController } from './audio/denoiseController';
import type { ScreenShareHint, ShareStartOptions } from './contract';
import { shareCaptureFps } from './shareBudget';
import {
  DEFAULT_AUDIO_PREFERENCES, DEFAULT_VIDEO_PREFERENCES, IDLE_PROCESSING_STATUS,
  type AudioProcessingPreferences, type MediaProcessingStatus, type VideoEffectPreferences,
} from './settings/preferences';
import { createEffectController, type EffectController } from './video/effectController';

export interface LocalMediaController {
  /** Camera track as PUBLISHED — processed when an effect is running. */
  readonly cameraStream: MediaStream | null;
  /** Microphone as PUBLISHED — denoised when RNNoise is running. */
  readonly micStream: MediaStream | null;
  /** Untouched capture. Voice-activity detection listens here, not to the mix. */
  readonly rawMicStream: MediaStream | null;
  readonly screenStream: MediaStream | null;
  /**
   * The share is carrying SOUND — an audio track came back from the capture.
   *
   * False is the common case, not a failure: Windows offers system audio for a
   * whole-screen or a Chrome tab and not for an application window, Firefox
   * offers it for a tab only, and Safari does not offer it at all. The publisher
   * reads this to decide whether to publish a second track and to charge the
   * budget for it; the presenter is told plainly rather than left wondering why
   * their video is silent in the room.
   */
  readonly screenAudioAvailable: boolean;
  readonly processing: MediaProcessingStatus;
  setCamera(on: boolean, deviceId?: string): Promise<MediaStream | null>;
  setMic(on: boolean, deviceId?: string): Promise<MediaStream | null>;
  setVideoEffect(preferences: VideoEffectPreferences): Promise<void>;
  setAudioProcessing(preferences: AudioProcessingPreferences): Promise<void>;
  /**
   * `profile` is not decoration. The capture frame rate is derived from the
   * SAME budget the encoder is configured with, so the OS can never be asked to
   * composite 30 fps into an encoder a profile has already capped at 10.
   *
   * Sound is captured only when `options.computerSound` is set — see
   * `ShareStartOptions` for the echo that asking for it by default caused.
   */
  startScreenShare(
    hint: ScreenShareHint,
    profile: QualityProfile,
    options?: ShareStartOptions,
  ): Promise<MediaStream | null>;
  stopScreenShare(): void;
  stop(): void;
}

const CAMERA_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
};

/**
 * The share capture ceiling. CAPACITY-EX44 §1.2 budgets a share as 1920x1080 —
 * so this is not a downgrade, it is the study's own geometry being enforced at
 * the one place that can enforce it.
 *
 * Uncapped, `getDisplayMedia` returns the panel's backing store: 3456x2234 on a
 * retina Mac, 7.7 Mpx. The same 1 500 kbps budget then spreads to ~0.018
 * bits/pixel where legible text wants ~0.1, and `degradationPreference:
 * 'maintain-resolution'` forbids the encoder from shedding pixels, so it pays
 * with the quantizer instead — soft, mushy text at full resolution. Capping the
 * CAPTURE hands the downscale to the OS compositor's scaler, which is far
 * better at it than an encoder at its quantiser ceiling. `max` (not `ideal`)
 * because the OS must honour it; aspect ratio is preserved, so a 16:10 Mac
 * lands at 1728x1080 rather than being letterboxed.
 */
const SHARE_MAX = LAYER_SPECS.l4_1080p;

/** Chromium's share options. Engines that do not know a member ignore it. */
type DisplayMediaRequest = DisplayMediaStreamOptions & {
  systemAudio?: 'include' | 'exclude';
  selfBrowserSurface?: 'include' | 'exclude';
};

/**
 * A share WITH SOUND — asked for only when the presenter turned on "Include
 * computer sound", and asked for in the one shape that cannot be this call.
 *
 * `audio: true`, the bare boolean, deliberately: a CONSTRAINT OBJECT is what
 * browsers reject outright on `getDisplayMedia` (Firefox and Safari both do),
 * and a rejection here fails the whole share, video included. An engine or a
 * source that has no audio to give simply returns a stream without an audio
 * track, which is handled as an ordinary outcome, not an error.
 *
 * `systemAudio: 'exclude'`: a browser offers two kinds of sound with a share —
 * a TAB's own sound, and the SYSTEM's. The system's is everything the machine
 * plays, this call included, which is the echo `ShareStartOptions` describes. A
 * tab's sound is that tab and nothing else, and a video playing in a tab is
 * what this switch exists for. `selfBrowserSurface: 'exclude'`: the one tab
 * whose sound IS this call is this tab. Chromium honours both; Firefox and
 * Safari ignore dictionary members they do not know.
 *
 * The desktop client's override reads only `audio`
 * (`features/desktop/displayMedia.ts`): there, Windows' loopback is the only
 * sound Chromium offers, it does carry this call, and the picker says so
 * before anybody turns it on.
 */
async function captureWithSound(video: MediaTrackConstraints): Promise<MediaStream> {
  const request: DisplayMediaRequest = {
    video,
    audio: true,
    systemAudio: 'exclude',
    selfBrowserSurface: 'exclude',
  };
  return navigator.mediaDevices.getDisplayMedia(request).catch(async (error: unknown) => {
    /* Dismissing the chooser is a decision, and the caller's existing
       NotAllowedError / AbortError handling must still see it. Anything else
       is an engine refusing the request itself, and a silent share beats no
       share: retry once with the shape that has always worked. */
    const name = error instanceof Error ? error.name : '';
    if (name === 'NotAllowedError' || name === 'AbortError') throw error;
    /* NOT ON THE DESKTOP CLIENT. There, `getDisplayMedia` is our own override
       (`features/desktop/displayMedia.ts`), which has ALREADY tried the
       loopback capturer, already fallen back to video-only and already fallen
       back to the platform chooser — all under one source id and one picker.
       Calling it again is not a retry, it is a SECOND native source picker in
       front of somebody who has already chosen. */
    if (isDesktopShell()) throw error;
    return navigator.mediaDevices.getDisplayMedia({ video, audio: false });
  });
}

export function createLocalMedia(
  onProcessingChange: () => void = () => undefined,
): LocalMediaController {
  let rawCamera: MediaStream | null = null;
  let camera: MediaStream | null = null;
  let rawMic: MediaStream | null = null;
  let mic: MediaStream | null = null;
  let screen: MediaStream | null = null;
  /**
   * WHICH CAPTURE IS THE CURRENT ONE — because the reference alone cannot say.
   *
   * `stopScreen()` closes the SEQUENTIAL overwrite: press twice in a row and the
   * first stream is released before the second is asked for. It does NOT close
   * the concurrent one. Two calls that both run `stopScreen()` while `screen` is
   * still null both go on to assign it, and the first stream's tracks are never
   * `.stop()`ed — a live display capture with no reference anywhere in the app.
   * It survives Stop, it survives LEAVING THE ROOM, and nothing but closing the
   * tab ends it, because `stop()` can only reach the reference that overwrote it.
   *
   * Chrome and Edge cannot deliver the second press (their chooser is modal) and
   * the desktop shell cancels the first picker (`screenSources.requestScreenSource`).
   * FIREFOX can: its prompt is a non-modal doorhanger, and the voice room's Share
   * button has no in-app picker in front of it.
   *
   * So ownership is decided by this counter rather than by the variable. Every
   * release and every request bumps it, and a capture whose number has moved
   * under it is nobody's: it is stopped where it lands, which is the only moment
   * anything still holds it.
   */
  let screenGeneration = 0;
  /**
   * THE SAME COUNTER FOR THE MICROPHONE, AND FOR THE SAME REASON.
   *
   * `stopMic()` stops whatever `rawMic` is AT THE INSTANT IT RUNS. A mute that
   * lands while an unmute's `getUserMedia` is still in flight therefore stops
   * NOTHING — `rawMic` is still null — and the acquire then resolves and assigns
   * a live capture on top of it. `syncPublications()` reads the store and
   * reports MUTED, so the UI says muted, nothing is published, and the operating
   * system's microphone indicator stays lit with no reference anywhere in the
   * app that can switch it off: not the mute button, not leaving the room, not
   * `stop()`, which can only reach the reference that overwrote it.
   *
   * Reachable whenever the acquire is slower than the gap between two presses —
   * a Bluetooth headset waking, a first-time permission prompt, a loaded
   * machine. Executed at 12 of 48 two-press interleavings before this counter
   * existed. It has been "mitigated" twice by making the storm of calls less
   * likely; the race was never in the storm, it was in the `await`.
   */
  let micGeneration = 0;

  let videoPreferences: VideoEffectPreferences = DEFAULT_VIDEO_PREFERENCES;
  let audioPreferences: AudioProcessingPreferences = DEFAULT_AUDIO_PREFERENCES;

  const effects: EffectController = createEffectController(onProcessingChange);
  const denoise: DenoiseController = createDenoiseController(onProcessingChange);

  const rebuildCamera = async (): Promise<void> => {
    const source = rawCamera?.getVideoTracks()[0] ?? null;
    if (!source) {
      effects.stop();
      camera = null;
      return;
    }
    await effects.apply(source, videoPreferences);
    camera = new MediaStream([effects.track ?? source]);
  };

  const rebuildMic = async (): Promise<void> => {
    if (!rawMic) {
      denoise.stop();
      mic = null;
      return;
    }
    await denoise.apply(rawMic, audioPreferences);
    const track = denoise.track ?? rawMic.getAudioTracks()[0] ?? null;
    mic = track ? new MediaStream([track]) : null;
  };

  const stopScreen = (): void => {
    screenGeneration += 1;
    for (const track of screen?.getTracks() ?? []) track.stop();
    screen = null;
  };

  const stopCamera = (): void => {
    effects.stop();
    for (const track of rawCamera?.getTracks() ?? []) track.stop();
    rawCamera = null;
    camera = null;
  };

  const stopMic = (): void => {
    /* Bumped BEFORE anything is torn down, so a capture that is still in flight
       is already superseded by the time it resolves. See `micGeneration`. */
    micGeneration += 1;
    denoise.stop();
    for (const track of rawMic?.getTracks() ?? []) track.stop();
    rawMic = null;
    mic = null;
  };

  return {
    get cameraStream() {
      return camera;
    },
    get micStream() {
      return mic;
    },
    get rawMicStream() {
      return rawMic;
    },
    get screenStream() {
      return screen;
    },
    get screenAudioAvailable() {
      return (screen?.getAudioTracks().length ?? 0) > 0;
    },
    get processing(): MediaProcessingStatus {
      if (!rawMic && !rawCamera) return IDLE_PROCESSING_STATUS;
      return { denoise: denoise.runtime, effect: effects.status };
    },

    async setCamera(on, deviceId) {
      stopCamera();
      if (!on) return null;
      rawCamera = await navigator.mediaDevices.getUserMedia({
        video: deviceId
          ? { ...CAMERA_CONSTRAINTS, deviceId: { exact: deviceId } }
          : CAMERA_CONSTRAINTS,
      });
      await rebuildCamera();
      return camera;
    },

    async setMic(on, deviceId) {
      stopMic();
      if (!on) return null;
      /* The generation this request owns. `stopMic` above has just bumped it,
         so anything that bumps it again — another request, or a mute — has
         superseded this one. */
      const generation = micGeneration;
      const captured = await navigator.mediaDevices.getUserMedia({
        audio: micConstraints(audioPreferences, deviceId),
      });
      /* SUPERSEDED WHILE THE DEVICE WAS OPENING — so this capture is nobody's,
         and stopping it HERE is the last moment anything can. Assigning it to
         `rawMic` would hand the user a live microphone they have already
         switched off, with nothing left holding a reference to it.

         `null` rather than a throw: every caller of `setMic` reads the result
         as "the microphone stream, or none", and none is the truth — the
         request that superseded this one has already decided what the
         microphone is doing. `startScreenShare` throws instead because ITS
         callers distinguish "you cancelled" from "it failed"; this one has no
         such conversation to have. */
      if (generation !== micGeneration) {
        for (const track of captured.getTracks()) track.stop();
        return null;
      }
      rawMic = captured;
      await rebuildMic();
      return mic;
    },

    async setVideoEffect(preferences) {
      videoPreferences = preferences;
      await rebuildCamera();
    },

    async setAudioProcessing(preferences) {
      const wasRnnoise = audioPreferences.noiseSuppression === 'rnnoise';
      audioPreferences = preferences;
      // Echo cancellation and gain are LIVE constraints — only a change of
      // denoiser has to rebuild the worklet graph.
      if (!wasRnnoise && preferences.noiseSuppression !== 'rnnoise') {
        await denoise.apply(rawMic, preferences);
        return;
      }
      await rebuildMic();
    },

    async startScreenShare(hint, profile, options = {}) {
      const computerSound = options.computerSound === true;
      /*
       * RELEASE THE PREVIOUS CAPTURE FIRST — the same line `setCamera` and
       * `setMic` open with, and the only one of the three that was missing.
       *
       * Without it `screen` is simply OVERWRITTEN below and the tracks it held
       * are never `.stop()`ed: a second press leaves a live display capture
       * with no reference anywhere in the app, so nothing can ever stop it and
       * the OS share indicator stays lit until the tab is closed. That needs a
       * state where the Share button offers "share" while a capture is already
       * running, which is exactly what a provider rejoin, a host's Stop and a
       * refused publish each produce.
       *
       * The button not to reach here is fixed too (`useShareController.toggle`
       * ends a capture that exists), but a leak this cheap to close should not
       * depend on a caller getting it right.
       */
      stopScreen();
      /* The generation this request owns. `stopScreen` above has just bumped
         it, so anything that bumps it again — another request, or a release —
         has superseded this one. See `screenGeneration`. */
      const generation = screenGeneration;
      // ONE source of truth with the encoder: `shareBudget` decides both.
      const fps = shareCaptureFps(profile, hint);
      const video: MediaTrackConstraints = {
        width: { max: SHARE_MAX.width },
        height: { max: SHARE_MAX.height },
        frameRate: { ideal: fps, max: fps },
      };

      /*
       * SOUND ONLY WHEN THE PRESENTER TURNED IT ON — "Include computer sound"
       * in the share picker, off every time it opens.
       *
       * Every share used to ask for it, and on Windows that is the echo people
       * reported: the only system sound there is to capture is the WHOLE
       * MACHINE'S OUTPUT, which includes this call. Every other participant's
       * voice came out of the presenter's speakers, went into the capture and
       * was sent straight back to them as the share's soundtrack, half a second
       * after they had said it. The SFU log shows it on real calls: a share
       * published with a `screen_share_audio` track, from the desktop client,
       * for the whole length of the call.
       */
      const captured = computerSound
        ? await captureWithSound(video)
        : await navigator.mediaDevices.getDisplayMedia({ video, audio: false });

      /*
       * SUPERSEDED WHILE THE CHOOSER WAS OPEN — so this capture is nobody's.
       *
       * Assigning it to `screen` here would drop the reference that the OTHER
       * caller (or `stop()`) is holding, and that stream's tracks would then be
       * unreachable and unstoppable for the life of the tab. Stopping it HERE is
       * the last moment anything can. The caller is told with `AbortError`,
       * which every Share path in the app already reads as "this start ended
       * without a share" and reports no failure for.
       */
      if (generation !== screenGeneration) {
        for (const track of captured.getTracks()) track.stop();
        const superseded = new Error('The screen capture was superseded.');
        superseded.name = 'AbortError';
        throw superseded;
      }
      /* NOT ASKED FOR, NOT SENT. The publisher puts whatever audio track the
         capture holds on the wire, so a sound this share did not ask for —
         an engine, or the desktop override, handing one back anyway — is
         stopped here rather than trusted to be absent. */
      if (!computerSound) {
        for (const sound of captured.getAudioTracks()) {
          captured.removeTrack(sound);
          sound.stop();
        }
      }
      screen = captured;

      const track = captured.getVideoTracks()[0];
      if (track && 'contentHint' in track) track.contentHint = hint;

      /* Processing belongs on a VOICE, never on a soundtrack: auto gain pumps
         music, and noise suppression treats an instrument as noise. Best-effort
         — an engine that will not take these still gives us the audio.

         ECHO CANCELLATION IS THE EXCEPTION on the desktop client that captures
         system sound through it: there it is what takes this call's own audio
         back out of the soundtrack (`features/desktop/displayMedia.ts`), and
         switching it off here would put the echo straight back. */
      const audioTrack = captured.getAudioTracks()[0];
      if (audioTrack) {
        void audioTrack
          .applyConstraints({
            autoGainControl: false,
            echoCancellation: hasEchoCancelledSystemAudio(),
            noiseSuppression: false,
          })
          .catch(() => undefined);
      }
      return captured;
    },

    stopScreenShare: stopScreen,

    stop() {
      stopCamera();
      stopMic();
      stopScreen();
    },
  };
}
